"use client";

import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import "./ClientModal.css";

import { FaRegUser, FaWhatsapp, FaRegBuilding } from "react-icons/fa";
import { MdOutlineEmail, MdOutlineAttachMoney, MdFamilyRestroom } from "react-icons/md";
import { RxInstagramLogo } from "react-icons/rx";
import { LuCalendarDays } from "react-icons/lu";
import { GiModernCity } from "react-icons/gi";
import { IoColorPaletteOutline } from "react-icons/io5";
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import { CiPhone } from "react-icons/ci";
import { REST_API_URL } from "../../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

const getInitialFormValues = () => ({
  "Client name": "",
  "Email Address": "",
  Instagram: "",
  "Phone Number": "",
  "WhatsApp number": "",
  "Company name": "",
  "TAX ID": "",
  Country: "",
  "About Service Notes": "",
  "Other Notes": "",
});

export default function AddClientModal({ onClose, client, onUpdated }) {
  const isEditing = Boolean(client?.id);
  const isSubmittingRef = useRef(false);
  const initializedClientIdRef = useRef(null);

  const now = new Date();

  const [familyStatus, setFamilyStatus] = useState("");
  const [hobbies, setHobbies] = useState([]);
  const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
  const [showHobbiesDropdown, setShowHobbiesDropdown] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [birthday, setBirthday] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [formValues, setFormValues] = useState(getInitialFormValues());
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const familyOptions = [
    "Single",
    "Married",
    "In a relationship",
    "Engaged",
    "Separated",
    "Divorced",
  ];

  const hobbyOptions = [
    "Reading",
    "Series",
    "Anime",
    "Festivals",
    "Martial arts",
    "Cooking",
  ];

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  useEffect(() => {
    if (!client?.id) {
      initializedClientIdRef.current = null;
      setFormValues(getInitialFormValues());
      setFormErrors({});
      setFamilyStatus("");
      setHobbies([]);
      setBirthday("");
      setYear(now.getFullYear());
      setMonth(now.getMonth());
      setSelectedDay(null);
      return;
    }

    if (initializedClientIdRef.current === client.id) {
      return;
    }

    initializedClientIdRef.current = client.id;

    setFormValues({
      "Client name": client.name || "",
      "Email Address": client.email || "",
      Instagram: client.instagram_username || "",
      "Phone Number": client.phone_number || "",
      "WhatsApp number": client.whatsapp_number || client.whatapp_number || "",
      "Company name": client.company_name || "",
      "TAX ID": client.tax_id || "",
      Country: client.country || "",
      "About Service Notes": client.about_service_notes || "",
      "Other Notes": client.other_notes || "",
    });

    setFormErrors({});
    setFamilyStatus(client.family_status || "");
    setHobbies(
      client.hobbies
        ? String(client.hobbies)
            .split(",")
            .map((h) => h.trim())
            .filter(Boolean)
        : []
    );

    if (client.birthday) {
      const [y, m, d] = String(client.birthday).split("-");
      const parsedYear = Number(y);
      const parsedMonth = Number(m) - 1;
      const parsedDay = Number(d);

      setBirthday(
        `${String(parsedDay).padStart(2, "0")}/${String(parsedMonth + 1).padStart(2, "0")}/${parsedYear}`
      );
      setYear(parsedYear);
      setMonth(parsedMonth);
      setSelectedDay(parsedDay);
    } else {
      setBirthday("");
      setYear(now.getFullYear());
      setMonth(now.getMonth());
      setSelectedDay(null);
    }
  }, [client?.id]);

  useEffect(() => {
    if (selectedDay && selectedDay > daysInMonth) {
      setSelectedDay(daysInMonth);
    }
  }, [selectedDay, daysInMonth]);

  const closeAllDropdowns = () => {
    setShowFamilyDropdown(false);
    setShowHobbiesDropdown(false);
    setShowCalendar(false);
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
  };

  const toggleFamilyDropdown = () => {
    setShowFamilyDropdown((prev) => !prev);
    setShowHobbiesDropdown(false);
    setShowCalendar(false);
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
  };

  const toggleHobbiesDropdown = () => {
    setShowHobbiesDropdown((prev) => !prev);
    setShowFamilyDropdown(false);
    setShowCalendar(false);
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
  };

  const toggleCalendar = () => {
    setShowCalendar((prev) => !prev);
    setShowFamilyDropdown(false);
    setShowHobbiesDropdown(false);
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
  };

  const handleSaveDate = () => {
    if (!selectedDay) {
      toast.error("Select a day first");
      return;
    }

    const formatted = `${String(selectedDay).padStart(2, "0")}/${String(
      month + 1
    ).padStart(2, "0")}/${year}`;

    setBirthday(formatted);
    setShowCalendar(false);
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
  };

  const handleInputChange = (label, value) => {
    let error = "";
    let newValue = value;

    if (label.includes("Phone") || label.includes("WhatsApp")) {
      if (/[a-zA-Z]/.test(value)) {
        error = "Only numbers allowed";
      }
      newValue = value.replace(/[^\d+()\-\s]/g, "");
    }

    if (label.includes("Email")) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        error = "Invalid email format";
      }
    }

    setFormValues((prev) => ({
      ...prev,
      [label]: newValue,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [label]: error,
    }));
  };

  const validateForm = () => {
    const requiredFields = ["Client name"];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formValues[field] || !String(formValues[field]).trim()) {
        newErrors[field] = "This field is required";
      }
    });

    if (
      formValues["Email Address"] &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues["Email Address"])
    ) {
      newErrors["Email Address"] = "Invalid email format";
    }

    if (
      formValues["Phone Number"] &&
      /[a-zA-Z]/.test(formValues["Phone Number"])
    ) {
      newErrors["Phone Number"] = "Only numbers allowed";
    }

    if (
      formValues["WhatsApp number"] &&
      /[a-zA-Z]/.test(formValues["WhatsApp number"])
    ) {
      newErrors["WhatsApp number"] = "Only numbers allowed";
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSaving || isSubmittingRef.current) return;

    if (!validateForm()) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) {
      toast.error("Authorization token not found");
      return;
    }

    isSubmittingRef.current = true;
    setIsSaving(true);

    const payload = {
      name: formValues["Client name"]?.trim() || "",
      email: formValues["Email Address"]?.trim() || "",
      instagram_username: formValues["Instagram"]?.trim() || "",
      phone_number: formValues["Phone Number"]?.trim() || "",
      whatsapp_number: formValues["WhatsApp number"]?.trim() || "",
      company_name: formValues["Company name"]?.trim() || "",
      tax_id: formValues["TAX ID"]?.trim() || "",
      birthday: birthday ? birthday.split("/").reverse().join("-") : null,
      country: formValues["Country"]?.trim() || "",
      family_status: familyStatus || "",
      hobbies: hobbies.join(", "),
      about_service_notes: formValues["About Service Notes"] || "",
      other_notes: formValues["Other Notes"] || "",
    };

    const headers = buildHeaders(token);

    const url = isEditing
      ? `${BASE_URL}/client/client-view/${client.id}/`
      : `${BASE_URL}/client/client-create/`;

    const method = isEditing ? "PATCH" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        const messages = [];

        if (data && typeof data === "object") {
          Object.entries(data).forEach(([key, val]) => {
            if (Array.isArray(val)) {
              val.forEach((msg) => messages.push(`${key}: ${msg}`));
            } else if (typeof val === "string") {
              messages.push(`${key}: ${val}`);
            }
          });
        }

        if (messages.length) {
          messages.forEach((msg) => toast.error(msg));
        } else {
          toast.error(`Error ${res.status}: Something went wrong`);
        }
        return;
      }

      if (!isEditing && typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "new_client_added");
      }

      toast.success(isEditing ? "Client updated successfully!" : "Client added successfully!");

      if (typeof onUpdated === "function") {
        onUpdated();
      }

      onClose();
    } catch (err) {
      console.error("Client submit error:", err);
      toast.error("Network error — check console");
    } finally {
      isSubmittingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <div className="add-clients__overlay" onClick={closeAllDropdowns}>
      <div className="add-clients" onClick={(e) => e.stopPropagation()}>
        <div className="add-clients__header">
          <h2>{isEditing ? "Edit Client" : "Add Client"}</h2>
          <button className="add-clients__close-btn" onClick={onClose} type="button">
            ✕
          </button>
        </div>

        <form className="add-clients__form" onSubmit={handleSubmit}>
          <div className="add-clients__row">
            <InputField
              icon={<FaRegUser />}
              label="Client name"
              placeholder="Name Surname"
              value={formValues["Client name"] || ""}
              error={formErrors["Client name"]}
              onChange={(v) => handleInputChange("Client name", v)}
            />
            <InputField
              icon={<MdOutlineEmail />}
              label="Email Address"
              placeholder="Randommail@gmail.com"
              value={formValues["Email Address"] || ""}
              error={formErrors["Email Address"]}
              onChange={(v) => handleInputChange("Email Address", v)}
            />
          </div>

          <div className="add-clients__row">
            <InputField
              icon={<RxInstagramLogo />}
              label="Instagram"
              placeholder="Username"
              value={formValues["Instagram"] || ""}
              onChange={(v) => handleInputChange("Instagram", v)}
            />
            <InputField
              icon={<CiPhone />}
              label="Phone Number"
              placeholder="+1 (31)-333-124-32"
              value={formValues["Phone Number"] || ""}
              error={formErrors["Phone Number"]}
              onChange={(v) => handleInputChange("Phone Number", v)}
            />
          </div>

          <div className="add-clients__row">
            <div className="add-clients__input-field">
              <label>Birthday</label>
              <div
                className="add-clients__input-box relative birthday_box"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCalendar();
                }}
              >
                <span className="add-clients__icon add-clients__icon_birthday">
                  <LuCalendarDays />
                  <IoIosArrowDown className="open_calendar_add_clients" />
                </span>

                <input
                  type="text"
                  readOnly
                  placeholder="Select client's birthday"
                  value={birthday}
                />

                {showCalendar && (
                  <div
                    className="add-clients__calendar-dropdown"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="add-clients__calendar-header">
                      <div className="add-clients__calendar-select">
                        <div className="calendar-select-group">
                          <button
                            type="button"
                            className="add-clients__calendar-btn"
                            onClick={() => {
                              setShowYearDropdown((prev) => !prev);
                              setShowMonthDropdown(false);
                            }}
                          >
                            {year} <IoIosArrowDown />
                          </button>

                          {showYearDropdown && (
                            <div className="calendar-select-dropdown">
                              {Array.from({ length: 126 }, (_, i) => 1900 + i).map((y) => (
                                <div
                                  key={y}
                                  className={`calendar-select-item ${y === year ? "selected" : ""}`}
                                  onClick={() => {
                                    setYear(y);
                                    setShowYearDropdown(false);
                                  }}
                                >
                                  {y}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="calendar-select-group">
                          <button
                            type="button"
                            className="add-clients__calendar-btn"
                            onClick={() => {
                              setShowMonthDropdown((prev) => !prev);
                              setShowYearDropdown(false);
                            }}
                          >
                            {months[month]} <IoIosArrowDown />
                          </button>

                          {showMonthDropdown && (
                            <div className="calendar-select-dropdown">
                              {months.map((m, i) => (
                                <div
                                  key={m}
                                  className={`calendar-select-item ${i === month ? "selected" : ""}`}
                                  onClick={() => {
                                    setMonth(i);
                                    setShowMonthDropdown(false);
                                  }}
                                >
                                  {m}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="add-clients__calendar-grid">
                      {days.map((day) => (
                        <div
                          key={day}
                          className={`add-clients__calendar-day ${selectedDay === day ? "selected" : ""}`}
                          onClick={() => setSelectedDay(day)}
                        >
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className="add-clients__calendar-footer">
                      <button
                        type="button"
                        className="cancel"
                        onClick={() => {
                          setShowCalendar(false);
                          setShowYearDropdown(false);
                          setShowMonthDropdown(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button type="button" className="save" onClick={handleSaveDate}>
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <InputField
              icon={<GiModernCity />}
              label="Country"
              placeholder="Type your Client's country"
              value={formValues["Country"] || ""}
              onChange={(v) => handleInputChange("Country", v)}
            />
          </div>

          <div className="add-clients__row">
            <InputField
              icon={<FaWhatsapp />}
              label="WhatsApp number"
              placeholder="Type client's Whatsapp number"
              value={formValues["WhatsApp number"] || ""}
              error={formErrors["WhatsApp number"]}
              onChange={(v) => handleInputChange("WhatsApp number", v)}
            />
            <InputField
              icon={<FaRegBuilding />}
              label="Company name"
              placeholder="Type client's company name"
              value={formValues["Company name"] || ""}
              onChange={(v) => handleInputChange("Company name", v)}
            />
          </div>

          <div className="add-clients__row">
            <InputField
              icon={<MdOutlineAttachMoney />}
              label="TAX ID"
              placeholder="Type client's TAX ID"
              value={formValues["TAX ID"] || ""}
              onChange={(v) => handleInputChange("TAX ID", v)}
            />

            <div className="add-clients__input-field">
              <label>Family Status</label>
              <div className="add-clients__input-box relative">
                <span className="add-clients__icon">
                  <MdFamilyRestroom />
                </span>

                <div
                  className="add-clients__select-display"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFamilyDropdown();
                  }}
                >
                  {familyStatus || "Type client's family status"}
                  <span className="add-clients__arrow">
                    {showFamilyDropdown ? <IoIosArrowUp /> : <IoIosArrowDown />}
                  </span>
                </div>

                {showFamilyDropdown && (
                  <div className="add-clients__dropdown">
                    {familyOptions.map((opt) => (
                      <label key={opt} className="add-clients__option">
                        <span>{opt}</span>
                        <input
                          type="radio"
                          name="familyStatus"
                          className="family-status-input"
                          checked={familyStatus === opt}
                          onChange={() => {
                            setFamilyStatus(opt);
                            setShowFamilyDropdown(false);
                          }}
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="add-clients__input-field add-clients__full">
            <label>Hobbies</label>
            <div className="add-clients__input-box relative">
              <span className="add-clients__icon">
                <IoColorPaletteOutline />
              </span>

              <div
                className="add-clients__select-display"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHobbiesDropdown();
                }}
              >
                {hobbies.length > 0 ? hobbies.join(", ") : "Select client's hobbies"}
                <span className="add-clients__arrow">
                  {showHobbiesDropdown ? <IoIosArrowUp /> : <IoIosArrowDown />}
                </span>
              </div>

              {showHobbiesDropdown && (
                <div className="add-clients__dropdown">
                  {hobbyOptions.map((opt) => (
                    <label key={opt} className="add-clients__option">
                      <span>{opt}</span>
                      <input
                        type="checkbox"
                        className="hobbies-checkbox"
                        checked={hobbies.includes(opt)}
                        onChange={() =>
                          setHobbies((prev) =>
                            prev.includes(opt)
                              ? prev.filter((h) => h !== opt)
                              : [...prev, opt]
                          )
                        }
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="add-clients__input-field add-clients__full">
            <label>About Service Notes</label>
            <textarea
              placeholder="Description"
              value={formValues["About Service Notes"] || ""}
              onChange={(e) => handleInputChange("About Service Notes", e.target.value)}
            />
          </div>

          <div className="add-clients__input-field add-clients__full">
            <label>Other Notes</label>
            <textarea
              placeholder="Description"
              value={formValues["Other Notes"] || ""}
              onChange={(e) => handleInputChange("Other Notes", e.target.value)}
            />
          </div>

          <div className="add-clients__button-row">
            <button
              type="button"
              className="add-clients__cancel-btn"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>

            <button type="submit" className="add-clients__add-btn" disabled={isSaving}>
              {isSaving ? "Processing..." : isEditing ? "Save Changes" : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const InputField = ({
  icon,
  label,
  placeholder,
  value = "",
  onChange,
  error,
}) => (
  <div className="add-clients__input-field">
    <label>{label}</label>
    <div className={`add-clients__input-box ${error ? "error" : ""}`}>
      <span className="add-clients__icon">{icon}</span>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
    {error ? <p className="input-error-text">{error}</p> : null}
  </div>
);